/**
 * Composable for managing LocalStorage.
 * It mimics the API of useCookies but stores data locally in the browser.
 * IMPORTANT: Data stored here is NEVER sent to the server in HTTP headers.
 */
export const useLocalStorage = () => {
  // check if we are in a browser environment (avoids SSR crashes)
  const isBrowser = typeof window !== 'undefined';

  /**
   * Retrieves and parses a value from localStorage.
   * If the value was stored with an expiration, it will be automatically
   * removed and `null` returned once it expires.
   * @param key The key of the item to retrieve.
   * @returns The parsed value, or null if the item doesn't exist or has expired.
   */
  const get = <T = any>(key: string): T | null => {
    if (!isBrowser) {
      return null;
    }

    const storedValue = localStorage.getItem(key);
    if (storedValue === null) {
      return null;
    }

    // Try to parse it as JSON, if it fails, return the raw string
    let parsed: unknown;
    let isJson = false;
    try {
      parsed = JSON.parse(storedValue);
      isJson = true;
    } catch {
      // not JSON, fall back to raw value below
    }

    // Handle values stored with expiration metadata
    if (
      isJson &&
      parsed &&
      typeof parsed === 'object' &&
      '__bv_expires__' in (parsed as Record<string, unknown>) &&
      '__bv_value__' in (parsed as Record<string, unknown>)
    ) {
      const wrapper = parsed as { __bv_expires__: number; __bv_value__: T };
      const { __bv_expires__, __bv_value__ } = wrapper;

      if (typeof __bv_expires__ === 'number' && Date.now() > __bv_expires__) {
        // Value has expired; remove it and return null
        localStorage.removeItem(key);
        return null;
      }

      return __bv_value__;
    }

    if (isJson) {
      return parsed as T;
    }

    return storedValue as unknown as T;
  };

  /**
   * Sets a value in localStorage.
   * - Objects and arrays are automatically stringified.
   * - When `expires` (in seconds) is provided, the value is wrapped with
   *   expiration metadata and will be automatically removed after it expires.
   * @param {string} key The key of the item to set.
   * @param {any} value The value to store.
   * @param {number} [expires] Optional expiration time in seconds. If omitted, the value never expires.
   */
  const set = (key: string, value: any, expires?: number): void => {
    if (!isBrowser) {
      return;
    }

    let valueToStore: string;

    if (typeof expires === 'number') {
      const wrapper = {
        __bv_expires__: Date.now() + expires * 1000,
        __bv_value__: value,
      };
      valueToStore = JSON.stringify(wrapper);
    } else {
      // If it's an object/array, stringify it.
      // If it's a string/number/boolean, we store it directly (converted to string by localStorage)
      valueToStore = typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value);
    }

    localStorage.setItem(key, valueToStore);
  };

  /**
   * Removes an item from localStorage.
   * @param key The key of the item to remove.
   */
  const remove = (key: string): void => {
    if (!isBrowser) {
      return;
    }
    localStorage.removeItem(key);
  };

  /**
   * Removes ALL items from localStorage for this domain.
   * Use with caution.
   */
  const removeAll = (): void => {
    if (!isBrowser) {
      return;
    }
    localStorage.clear();
  };

  return {
    get,
    set,
    remove,
    removeAll,
  };
};
